import { ApiError, apiRequest } from "@/lib/api";

export type AdminScoreFilter = "all" | "low" | "medium" | "high";
export type AdminViewMode = "visitor" | "presenter";

export type AdminLeadApi = {
  id: string;
  sessionId: string;
  email: string;
  organisationName: string | null;
  regionId: string | null;
  businessSector: string | null;
  createdAt: string;
  annualEbitda: number;
  totalTrapped: number;
  tciScore: number;
  laborWaste: number;
  yieldPenaltyWaste: number;
  untappedValue: number;
};

export type AdminDashboardStatsApi = {
  totalInteractions: number;
  interactionsThisWeek: number;
  totalLeads: number;
  leadsThisWeek: number;
  totalTrappedMargin: number;
};

export type AdminDashboardResponse = {
  stats: AdminDashboardStatsApi;
  leads: {
    items: AdminLeadApi[];
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  };
};

export type AdminVisitApi = {
  id: string;
  sessionId: string;
  email: string | null;
  organisationName: string | null;
  regionId: string | null;
  businessSector: string | null;
  currentStep: string | null;
  status: string;
  workflowId: string | null;
  tciScore: number | null;
  calculationId: string | null;
  createdAt: string;
  lastSeenAt: string;
};

export type AdminVisitsResponse = {
  items: AdminVisitApi[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
};

export type AdminDashboardQuery = {
  page?: number;
  pageSize?: number;
  search?: string;
  score?: AdminScoreFilter;
  viewMode?: AdminViewMode;
};

export type AdminVisitsQuery = {
  page?: number;
  pageSize?: number;
  search?: string;
};

function toQueryString(query: AdminDashboardQuery | AdminVisitsQuery): string {
  const params = new URLSearchParams();
  if (query.page != null) params.set("page", String(query.page));
  if (query.pageSize != null) params.set("pageSize", String(query.pageSize));
  if (query.search?.trim()) params.set("search", query.search.trim());
  if ("score" in query && query.score && query.score !== "all") {
    params.set("score", query.score);
  }
  if ("viewMode" in query && query.viewMode && query.viewMode !== "visitor") {
    params.set("viewMode", query.viewMode);
  }
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export const adminApi = {
  getDashboard: (query: AdminDashboardQuery = {}) =>
    apiRequest<AdminDashboardResponse>(
      `/admin/dashboard${toQueryString(query)}`,
    ),
  getVisits: (query: AdminVisitsQuery = {}) =>
    apiRequest<AdminVisitsResponse>(`/admin/visits${toQueryString(query)}`),
  deleteLead: (id: string) =>
    apiRequest<{ id: string }>(`/admin/leads/${encodeURIComponent(id)}`, {
      method: "DELETE",
    }),
};

export { ApiError };
